import {
  expandCellRange,
  highlightColorCssVar,
  rangeFromTextOffsets,
  type HighlightCellAnchor,
  type HighlightColor,
} from '@/utils/fileHighlights';

export interface SheetHighlightMark {
  id: string;
  cellAnchor: HighlightCellAnchor | null;
  color: HighlightColor;
  selected: boolean;
  hovered: boolean;
}

const MARK_SELECTOR = 'mark[data-fp-hl]';

function unwrapMarks(root: HTMLElement): void {
  for (const mark of [...root.querySelectorAll(MARK_SELECTOR)]) {
    const parent = mark.parentNode;
    if (!parent) continue;
    while (mark.firstChild) parent.insertBefore(mark.firstChild, mark);
    parent.removeChild(mark);
  }
  root.normalize();
}

function clearCellPaint(root: HTMLElement): void {
  for (const cell of root.querySelectorAll<HTMLElement>('td.fp-sheet-hl')) {
    cell.classList.remove('fp-sheet-hl', 'is-on', 'is-hov');
    delete cell.dataset.hlId;
    cell.style.removeProperty('--hc');
  }
  unwrapMarks(root);
}

function paintCell(cell: HTMLElement, mark: SheetHighlightMark): void {
  cell.classList.add('fp-sheet-hl');
  cell.classList.toggle('is-on', mark.selected);
  cell.classList.toggle('is-hov', mark.hovered && !mark.selected);
  cell.dataset.hlId = mark.id;
  cell.style.setProperty('--hc', highlightColorCssVar(mark.color));
}

function paintPartial(cell: HTMLElement, mark: SheetHighlightMark, anchor: HighlightCellAnchor): boolean {
  if (anchor.start === undefined || anchor.end === undefined) return false;
  const range = rangeFromTextOffsets(cell, anchor.start, anchor.end);
  if (!range) return false;
  const doc = cell.ownerDocument;
  const el = doc.createElement('mark');
  el.dataset.fpHl = '1';
  el.dataset.hlId = mark.id;
  el.className = 'fp-doc-hl';
  if (mark.selected) el.classList.add('is-on');
  if (mark.hovered && !mark.selected) el.classList.add('is-hov');
  el.style.setProperty('--hc', highlightColorCssVar(mark.color));
  try {
    range.surroundContents(el);
    return true;
  } catch {
    try {
      el.append(range.extractContents());
      range.insertNode(el);
      return Boolean(el.textContent);
    } catch {
      return false;
    }
  }
}

/** Paint saved xlsx marks onto the preview table. The cell text stays in place. */
export function syncSheetHighlightMarks(root: HTMLElement, marks: SheetHighlightMark[]): number {
  clearCellPaint(root);
  let applied = 0;
  for (const mark of marks) {
    const anchor = mark.cellAnchor;
    if (!anchor) continue;
    const refs = expandCellRange(anchor.range);
    if (refs.length === 0) continue;
    const partial = refs.length === 1 && anchor.start !== undefined && anchor.end !== undefined;
    let painted = false;
    for (const ref of refs) {
      const cell = root.querySelector<HTMLElement>(`td[data-cell="${ref}"]`);
      if (!cell) continue;
      if (partial) {
        painted = paintPartial(cell, mark, anchor) || painted;
      } else {
        paintCell(cell, mark);
        painted = true;
      }
    }
    if (painted) applied += 1;
  }
  return applied;
}
